import { Sparkles, Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n';
import { useLanguage } from '../i18n';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const { t } = useTranslation();
  const { language, getLocalizedPath, getCurrentPath } = useLanguage();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAlternateLanguagePath = () => {
    const currentPath = getCurrentPath();
    return language === 'ro' ? `/en${currentPath}` : currentPath;
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#0E0A31]/95 backdrop-blur-xl border-b border-slate-800' : 'bg-[#0E0A31]/80 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to={getLocalizedPath('/')} className="flex items-center group cursor-pointer">
            <img
              src="/grayscale_transparent_nobuffer.png"
              alt="ControlMax"
              className="h-12 w-auto group-hover:scale-105 transition-transform"
              width={156}
              height={48}
              loading="eager"
            />
          </Link>

          <div className="hidden lg:flex space-x-4 xl:space-x-6">
            <Link to={getLocalizedPath('/centre-cost')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.costCenters}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/hr-pontaj')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.hrPontaj}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/logistica')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.logistics}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/achizitii')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.procurement}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/echipamente')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.equipment}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/documente')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.documents}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to={getLocalizedPath('/despre')} className="text-slate-300 hover:text-white transition-colors font-medium relative group text-sm whitespace-nowrap">
              {t.nav.about}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>

          <div className="flex items-center space-x-2">
            <div className="hidden lg:block relative" ref={dropdownRef}>
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                className="flex items-center space-x-1 text-slate-300 hover:text-white transition-colors font-medium p-2 rounded-lg hover:bg-slate-800/50"
                aria-label="Switch language"
              >
                <Globe className="h-5 w-5" />
                <ChevronDown className={`h-4 w-4 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {languageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-[#0E0A31] border border-slate-700 rounded-lg shadow-xl overflow-hidden">
                  <Link
                    to={getCurrentPath()}
                    onClick={() => setLanguageDropdownOpen(false)}
                    className={`block px-4 py-3 text-sm transition-colors ${language === 'ro' ? 'bg-blue-600/20 text-white' : 'text-slate-300 hover:bg-slate-800/50'}`}
                  >
                    Română
                  </Link>
                  <Link
                    to={`/en${getCurrentPath()}`}
                    onClick={() => setLanguageDropdownOpen(false)}
                    className={`block px-4 py-3 text-sm transition-colors ${language === 'en' ? 'bg-blue-600/20 text-white' : 'text-slate-300 hover:bg-slate-800/50'}`}
                  >
                    English
                  </Link>
                </div>
              )}
            </div>

            <Link to={getLocalizedPath('/demo')} className="hidden lg:block relative group overflow-hidden px-4 py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-all whitespace-nowrap">
              <span className="relative z-10 flex items-center text-sm">
                {t.nav.demo}
                <Sparkles className="ml-1.5 h-4 w-4" />
              </span>
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white transition-colors"
              aria-label={mobileMenuOpen ? 'Închide meniul' : 'Deschide meniul'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0E0A31]/98 backdrop-blur-xl border-b border-slate-800">
          <div className="px-4 py-6 space-y-4">
            <Link
              to={getLocalizedPath('/centre-cost')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.costCenters}
            </Link>
            <Link
              to={getLocalizedPath('/hr-pontaj')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.hrPontaj}
            </Link>
            <Link
              to={getLocalizedPath('/logistica')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.logistics}
            </Link>
            <Link
              to={getLocalizedPath('/achizitii')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.procurement}
            </Link>
            <Link
              to={getLocalizedPath('/echipamente')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.equipment}
            </Link>
            <Link
              to={getLocalizedPath('/documente')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.documents}
            </Link>
            <Link
              to={getLocalizedPath('/despre')}
              className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t.nav.about}
            </Link>

            <div className="border-t border-slate-700 pt-4 mt-4">
              <div className="flex items-center space-x-2 text-slate-400 font-medium mb-3 text-sm">
                <Globe className="h-4 w-4" />
                <span>Limbă / Language</span>
              </div>
              <div className="space-y-2">
                <Link
                  to={getCurrentPath()}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-lg text-sm transition-colors ${language === 'ro' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-300'}`}
                >
                  Română
                </Link>
                <Link
                  to={`/en${getCurrentPath()}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-lg text-sm transition-colors ${language === 'en' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-300'}`}
                >
                  English
                </Link>
              </div>
            </div>

            <Link
              to={getLocalizedPath('/demo')}
              className="block w-full mt-4 px-6 py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-500 text-white text-center transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex items-center justify-center">
                {t.nav.demo}
                <Sparkles className="ml-2 h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
